import React, { useEffect, useState } from "react";
import classNames from "classnames";
import Checkbox from "@mui/material/Checkbox";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { periods, periodsStartsForConsumption } from "helpers/periods";
import Tooltip from "@mui/material/Tooltip";
import WarningIcon from "@mui/icons-material/SyncProblem";
import TemperatureIcon from "@mui/icons-material/DeviceThermostat";
import {
  ForecastPeriodData,
  ForecastTemperatureMatch,
  RowValue,
} from "api/models/Forecast";
import { ForecastRow, getForecastRowsForDate } from "helpers/timeIntervals";

import Box from "@mui/material/Box";

import styles from "./ForecastDataGrid.module.scss";
import ConsumptionForecastFixedTable from "./ConsumptionForecastFixedTable";

interface ForecastDataGridProps {
  isEditable: boolean;
  periodsData: ForecastPeriodData[];
  setPeriodsData: React.Dispatch<React.SetStateAction<ForecastPeriodData[]>>;
  setAverageLine: React.Dispatch<React.SetStateAction<number[]>>;
  setForecastLine: React.Dispatch<React.SetStateAction<number[]>>;
  tableMatrix: any;
  setTableMatrix: any;
  prognoseValues: any;
  hourlyForecast?: boolean;
  forecastDate: string;
  forecastTemperatures?: Record<string, number>;
  measuredTemperatures?: Record<string, number>;
  temperatureMatches?: ForecastTemperatureMatch[];
}

type GridUnit = "MW" | "C";

/** Mirrors DayBlock on the backend. */
const BLOCK_LABELS: Record<ForecastTemperatureMatch["block"], string> = {
  NIGHT: "Night",
  MORNING: "Morning",
  AFTERNOON: "Afternoon",
  EVENING: "Evening",
};

const BLOCK_HOURS: Record<ForecastTemperatureMatch["block"], string> = {
  NIGHT: "00:00-06:00",
  MORNING: "06:00-12:00",
  AFTERNOON: "12:00-18:00",
  EVENING: "18:00-24:00",
};

export const BLOCK_ORDER: ForecastTemperatureMatch["block"][] = [
  "NIGHT",
  "MORNING",
  "AFTERNOON",
  "EVENING",
];

const rankLabel = (rank: number) =>
  rank === 1 ? "closest match" : `${rank}${rank === 2 ? "nd" : "rd"} closest`;

/**
 * Each block ranks candidate days independently, so a day can win one block, several, or none. This
 * spells out which rankings a day landed on and how close it actually was.
 */
const explainMatches = (matches: ForecastTemperatureMatch[]) =>
  matches
    .map(
      (m) =>
        `${BLOCK_LABELS[m.block]} (${BLOCK_HOURS[m.block]}) — ${rankLabel(m.matchRank)}: ` +
        `${m.deltaSum}°C total difference over the 6 hours, worst hour ${m.deltaMax}°C.`,
    )
    .join(" ");

interface TableCell {
  cellIndex: number;
  reading: number;
  isMissingData: boolean;
  meteringPointsWithConsumption: number;
  meteringPointsTotal: number;
  active: boolean;
  selectedBasedOnTemperature: boolean;
  /** Whether the user may toggle this cell; see PrognosePeriodConsumption.selectable. */
  selectable: boolean;
  /** Measured temperature of this cell's day and hour; null when never measured. */
  temperature: number | null;
}

export interface TableRow {
  hour: number;
  minute: number;
  repeatedHour: boolean;
  readingIndex: number;
  readings: TableCell[];
  average: number;
  correction: number;
  forecast: number;
}

interface PeriodsData {
  isoDate: string;
  readings: {
    selected: boolean;
    totalConsumption: number;
    isMissingData: boolean;
    meteringPointsWithConsumption: number;
    meteringPointsTotal: number;
    selectedBasedOnTemperature: boolean;
    selectable: boolean;
  }[];
}

export const temperatureHour = (row: TableRow) =>
  String(row.hour).padStart(2, "0");

const readingIndexOf = (row: ForecastRow, hourlyForecast?: boolean) =>
  hourlyForecast ? row.hour : (row.hour * 60 + row.minute) / 15;

const hourlyRows: ForecastRow[] = periodsStartsForConsumption.map(
  (start, hour) => ({ start, end: "", hour, minute: 0, repeatedHour: false }),
);

const getTableMatrix = (
  rows: ForecastRow[],
  periodsData: PeriodsData[],
  prognoseValues: RowValue[],
  measuredTemperatures: Record<string, number>,
  hourlyForecast?: boolean,
): TableRow[] => {
  const tableData: TableRow[] = [];

  rows.map((row, rowIndex: number) => {
    const rowData: TableCell[] = [];
    let sum = 0;

    const readingIndex = readingIndexOf(row, hourlyForecast);
    const hour = String(row.hour).padStart(2, "0");

    periodsData.map((col, colIndex: number) => {
      const reading = col.readings[readingIndex];

      if (reading?.selected) {
        sum += reading?.totalConsumption;
      }

      rowData.push({
        cellIndex: rowIndex + colIndex,
        reading: reading?.totalConsumption,
        isMissingData: reading?.isMissingData,
        meteringPointsWithConsumption: reading?.meteringPointsWithConsumption,
        meteringPointsTotal: reading?.meteringPointsTotal,
        active: reading?.selected,
        selectedBasedOnTemperature:
          reading?.selectedBasedOnTemperature ?? false,
        selectable: reading?.selectable ?? true,
        temperature: measuredTemperatures[`${col.isoDate} ${hour}`] ?? null,
      });
    });

    const correction =
      prognoseValues.find(
        (pv) =>
          pv.hour === row.hour &&
          pv.minute === row.minute &&
          (pv.repeatedHour ?? false) === row.repeatedHour,
      )?.adjustment || 0;

    tableData.push({
      hour: row.hour,
      minute: row.minute,
      repeatedHour: row.repeatedHour,
      readingIndex,
      readings: rowData,
      average:
        rowData.filter((rd) => rd.active).length > 0
          ? Math.round(
              (sum / rowData.filter((rd) => rd.active).length) * 1000,
            ) / 1000
          : 0,
      correction: correction,
      forecast:
        rowData.filter((rd) => rd.active).length > 0
          ? Number(
              (
                sum / rowData.filter((rd) => rd.active).length +
                correction
              ).toFixed(3),
            )
          : Number(correction.toFixed(3)),
    });
  });

  return tableData;
};

const ForecastDataGrid = ({
  isEditable,
  periodsData,
  setAverageLine,
  setForecastLine,
  tableMatrix,
  setTableMatrix,
  prognoseValues,
  hourlyForecast,
  forecastDate,
  forecastTemperatures = {},
  measuredTemperatures = {},
  temperatureMatches = [],
}: ForecastDataGridProps) => {
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [unit, setUnit] = useState<GridUnit>("MW");
  const showTemperatures = unit === "C";

  const rows = hourlyForecast
    ? hourlyRows
    : getForecastRowsForDate(forecastDate);

  const MTUs = hourlyForecast
    ? periods
    : rows.map((row) => `${row.start} - ${row.end}`);

  const handleMouseDown = (event: any) => {
    if (event.button === 0) {
      setIsMouseDown(true);
    }
  };

  const handleMouseUp = () => {
    setIsMouseDown(false);
  };

  useEffect(() => {
    setTableMatrix(
      getTableMatrix(
        rows,
        periodsData,
        prognoseValues ?? [],
        measuredTemperatures,
        hourlyForecast,
      ),
    );
  }, [periodsData, measuredTemperatures]);

  useEffect(() => {
    const avgLine: number[] = [];
    const forecastLine: number[] = [];

    tableMatrix.map((row: TableRow) => {
      avgLine.push(row.average);
      forecastLine.push(row.forecast);
    });

    setAverageLine(avgLine);
    setForecastLine(forecastLine);
  }, [tableMatrix]);

  const isCheckboxChecked = (cellIndex: number) => {
    return tableMatrix.some((row: TableRow) => row.readings[cellIndex]?.active);
  };

  const matchesFor = (isoDate: string) =>
    temperatureMatches
      .filter((match) => match.date === isoDate)
      .sort(
        (a, b) => BLOCK_ORDER.indexOf(a.block) - BLOCK_ORDER.indexOf(b.block),
      );

  /** Whether the column carries a temperature match at all — drives the header icon, not the toggle. */
  const isTemperatureMatchedColumn = (cellIndex: number) => {
    return tableMatrix.some(
      (row: TableRow) => row.readings[cellIndex]?.selectedBasedOnTemperature,
    );
  };

  const handleToggleAllPeriods = (cellIndex: number) => {
    const tableMatrixClone: TableRow[] = [...tableMatrix];
    const checkboxChecked = isCheckboxChecked(cellIndex);

    // `selectable` already carries the scope: a legacy day marks every cell, a day that got in only
    // through temperature matching marks just the 6-hour block it matched on. Deriving the scope
    // here from `selectedBasedOnTemperature` instead narrowed legacy days that happened to also
    // match on temperature down to their matched block, stranding the other 18 hours.
    tableMatrixClone.forEach((row) => {
      if (!row.readings[cellIndex].selectable) {
        return;
      }

      row.readings[cellIndex].active = !checkboxChecked;
    });

    setTableMatrix(tableMatrixClone);

    tableMatrixClone.forEach((_, rowIndex) => {
      calculateAvgAndForecast(rowIndex);
    });
  };

  const calculateAvgAndForecast = (rowIndex: number) => {
    const tableMatrixClone: TableRow[] = [...tableMatrix];
    let sum = 0;

    tableMatrixClone[rowIndex].readings.map((reading: TableCell) => {
      if (reading.active) {
        sum += reading.reading;
      }
    });

    const activeCount = tableMatrixClone[rowIndex].readings.filter(
      (rd: TableCell) => rd.active,
    ).length;

    const avg =
      activeCount > 0 ? Math.round((sum / activeCount) * 1000) / 1000 : 0;

    const forecast =
      Math.round((avg + Number(tableMatrixClone[rowIndex].correction)) * 1000) /
      1000;

    tableMatrixClone[rowIndex].average = avg;
    tableMatrixClone[rowIndex].forecast = forecast;
  };

  const handleSelectCell = (rowIndex: number, cellIndex: number) => {
    const tableMatrixClone: TableRow[] = [...tableMatrix];
    const { readingIndex } = tableMatrixClone[rowIndex];
    const active = !tableMatrixClone[rowIndex].readings[cellIndex].active;

    // Both 03:xx rows of the DST change share a reading.
    tableMatrixClone.forEach((row, index) => {
      if (row.readingIndex === readingIndex) {
        row.readings[cellIndex].active = active;
        calculateAvgAndForecast(index);
      }
    });

    setTableMatrix(tableMatrixClone);
  };

  const handleSetCorrection = (value: number, rowIndex: number) => {
    const tableMatrixClone = [...tableMatrix];

    const average = tableMatrix[rowIndex].average;

    tableMatrix[rowIndex].correction = value;
    tableMatrix[rowIndex].forecast =
      Math.round((Number(value) + average) * 1000) / 1000;

    setTableMatrix(tableMatrixClone);
  };

  return (
    <Box>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={unit}
        onChange={(_, next: GridUnit | null) => next && setUnit(next)}
        // MUI uppercases button labels, which mangles unit symbols; render them verbatim.
        sx={{ mb: 1, "& .MuiToggleButton-root": { textTransform: "none" } }}
      >
        <ToggleButton value="MW">MW</ToggleButton>
        <ToggleButton value="C">°C</ToggleButton>
      </ToggleButtonGroup>

      {/* The scrollport the fixed table sticks inside — without it nothing can stay pinned. */}
      <Box
        display="flex"
        justifyContent="flex-start"
        alignItems="flex-start"
        sx={{ overflowX: "auto", overflowY: "hidden" }}
      >
        <ConsumptionForecastFixedTable
          MTUs={MTUs}
          tableMatrix={tableMatrix}
          forecastTemperatures={forecastTemperatures}
          isEditable={isEditable}
          handleSetCorrection={handleSetCorrection}
        />
        <div className={styles.forecastDataGrid}>
          <table
            className={classNames(styles.table, isEditable && styles.isEditing)}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            style={{ userSelect: "none" }}
          >
            <thead>
              <tr>
                {periodsData.map((date, cellIndex) => (
                  <th key={cellIndex}>
                    <div className={styles.dateCell}>
                      <p className={styles.date}>
                        {date.date}
                        {isTemperatureMatchedColumn(cellIndex) && (
                          <Tooltip
                            title="Picked because these hours resembled the forecast temperature. Shown for reference — not part of the average until you select it."
                            placement="top"
                          >
                            <TemperatureIcon
                              sx={{
                                marginLeft: "4px",
                                width: "16px",
                                height: "16px",
                                verticalAlign: "text-bottom",
                              }}
                              color="info"
                            />
                          </Tooltip>
                        )}
                      </p>
                      <p className={styles.day}>{date.day}</p>
                      {matchesFor(date.isoDate).length > 0 ? (
                        <Tooltip
                          title={explainMatches(matchesFor(date.isoDate))}
                          placement="top"
                        >
                          <p className={styles.matchedBlocks}>
                            {matchesFor(date.isoDate)
                              .map((match) => BLOCK_LABELS[match.block])
                              .join(" + ")}
                          </p>
                        </Tooltip>
                      ) : (
                        // Kept so both tables' header rows stay the same height.
                        <p className={styles.matchedBlocks}>&nbsp;</p>
                      )}
                      {isEditable && (
                        <span>
                          <Checkbox
                            size="small"
                            checked={isCheckboxChecked(cellIndex)}
                            sx={{ m: 0, p: 0 }}
                            onClick={() => handleToggleAllPeriods(cellIndex)}
                          />
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableMatrix.map((row: TableRow, rowIndex: number) => {
                return (
                  <tr key={rowIndex}>
                    {row.readings.map((cell, cellIndex) => (
                      <td
                        key={cellIndex}
                        className={classNames(
                          styles.data,
                          cell.active && styles.selected,
                          // Only the cells of the 6-hour block the day was matched on carry the flag,
                          // so the blue marks those MTUs rather than the whole column.
                          cell.selectedBasedOnTemperature &&
                            styles.temperatureMatched,
                        )}
                        onMouseDown={() => {
                          handleSelectCell(rowIndex, cellIndex);
                        }}
                        onMouseEnter={() => {
                          if (isMouseDown) {
                            handleSelectCell(rowIndex, cellIndex);
                          }
                        }}
                      >
                        <span
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          {showTemperatures
                            ? cell.temperature ?? "–"
                            : cell.reading}
                          {!showTemperatures && cell.isMissingData && (
                            <Tooltip
                              title={`Partial readings: ${cell.meteringPointsWithConsumption}/${cell.meteringPointsTotal}`}
                              placement="top"
                            >
                              <WarningIcon
                                sx={{
                                  marginLeft: "4px",
                                  width: "16px",
                                  height: "16px",
                                }}
                                color="error"
                              />
                            </Tooltip>
                          )}
                        </span>
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Box>
    </Box>
  );
};

export default ForecastDataGrid;
